import { BaseItem } from '@algolia/autocomplete-core';
import { AutocompleteComponents, HTMLTemplate } from '@algolia/autocomplete-js';
import {
  parseAlgoliaHitHighlight,
  RequesterDescription,
  SearchResponse,
  TransformResponse,
} from '@algolia/autocomplete-preset-algolia';
import { AutocompleteSource } from '@algolia/autocomplete-shared';
import { OnSelectParams } from '@algolia/autocomplete-shared/dist/esm/core/AutocompleteSource';
import { AutocompleteState } from '@algolia/autocomplete-shared/dist/esm/core/AutocompleteState';
import { MultipleQueriesQuery } from '@algolia/autocomplete-shared/dist/esm/preset-algolia/algoliasearch';
import { TransformedRequesterResponse } from '@algolia/autocomplete-shared/dist/esm/preset-algolia/createRequester';
import { Hit } from '@algolia/client-search';
import { AlgoliaSearchHelpers } from '@mode/algolia/domain';
import {
  capraIconBuildingEdit,
  capraIconBuildingView,
  capraIconCollection,
  capraIconDataset,
  capraIconLock,
  capraIconNewTab,
  capraIconReportBuilder,
} from '@mode/capra';
import { ErrorReporter } from '@mode/shared/contract-common';
import { SearchClient } from 'algoliasearch';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { sortCollectionResults } from './autocomplete-helpers';

export const SHOW_MORE_RESULTS_ID = '__show-more-results';

export class AutocompleteSourceFactory {
  constructor(
    private errorReporterService: ErrorReporter,
    private algoliaAppId: string,
    private algoliaApiKey: string,
    private analyticsService: AnalyticsService
  ) {}

  static footer({ html }: { html: HTMLTemplate }) {
    return html`<div class="footer">Cmd/ctrl + Enter to open in a new tab</div>`;
  }

  public createSource<TItem extends BaseItem>({
    searchClient,
    queries,
    showNoResults,
  }: {
    searchClient: SearchClient;
    queries: MultipleQueriesQuery[];
    showNoResults: boolean;
  }): AutocompleteSource<TItem> {
    return {
      getItems: () => (showNoResults ? [] : this.getItems<TItem>({ searchClient, queries })),
      getItemUrl: (...args) => this.getItemUrl(...args),
      onSelect: (...args) => this.onSelect(...args),
      sourceId: 'header-global-search-results',
      templates: {
        item: ({ item, components, html }) => this.item({ html, item, components }),
        footer: ({ html }) => AutocompleteSourceFactory.footer({ html }),
      },
    };
  }

  public getItems<TItem extends BaseItem>({
    searchClient,
    queries,
  }: {
    searchClient: SearchClient;
    queries: MultipleQueriesQuery[];
  }): RequesterDescription<TItem> {
    return this.initAlgoliaSearchRequesterDescription({ searchClient, queries });
  }

  public initAlgoliaSearchRequesterDescription<TItem extends BaseItem>({
    searchClient,
    queries,
  }: {
    searchClient: SearchClient;
    queries: MultipleQueriesQuery[];
  }): RequesterDescription<TItem> {
    const appId = this.algoliaAppId;
    const apiKey = this.algoliaApiKey;
    return AlgoliaSearchHelpers.getAlgoliaResults({
      apiKey,
      appId,
      queries,
      searchClient,
      transformResponse: (...params) => this.transformResponse(...params),
    });
  }

  public getItemUrl<TItem extends BaseItem>({
    item,
  }: {
    item: TItem;
    state: AutocompleteState<TItem>;
  }): string | undefined {
    return String(item.webPath);
  }

  public onSelect<TItem extends BaseItem>({ itemUrl, item, event }: OnSelectParams<TItem>) {
    if (itemUrl) {
      // track selection
      this.analyticsService.track(AnalyticsEvent.Name.Search, {
        location: 'Spotlight Search',
        action: 'Select Content',
        content_title: item.name,
        content_id: item.token,
      });

      // Open in a new tab when clicked.
      if (event.type === 'click') {
        window.open(itemUrl, '_blank');
      }
    } else {
      this.errorReporterService.notify({
        debugInfo: item,
        error: new Error('No url found for Algolia search result'),
        severity: 'error',
        unhandled: false,
      });
    }
  }

  private item<TItem extends BaseItem>({
    html,
    item,
    components,
  }: {
    html: HTMLTemplate;
    item: TItem;
    components: AutocompleteComponents;
  }) {
    const viewIcon = this.forceWidth(capraIconBuildingView.data);
    const queryIcon = this.forceWidth(capraIconBuildingEdit.data);
    const lockIcon = this.forceWidth(capraIconLock.data);
    const reportIcon = this.forceWidth(capraIconReportBuilder.data);
    const datasetIcon = this.forceWidth(capraIconDataset.data, 'var(--ds-purple-enhanced-300)');
    const newTabIcon = this.forceWidth(capraIconNewTab.data);
    const collectionIcon = this.forceWidth(capraIconCollection.data);

    const badges = this.parseBadges(item);

    if (item.objectID === SHOW_MORE_RESULTS_ID) {
      return html`
        <div class="aa-ItemWrapper show-more-link">
          <div class="aa-ItemContent">
            <div class="aa-ItemContentBody">${item.name}</div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="aa-ItemWrapper">
        <div class="aa-ItemContent">
          <div class="aa-ItemIcon aa-ItemIcon--alignTop ${item.reportType === 'Report' ? 'report-icon' : ''}">
            ${item.reportType === 'Report' && html([reportIcon] as any)}
            ${item.reportType === 'DatasetReport' && html([datasetIcon] as any)}
            ${item.spaceType && html([collectionIcon] as any)}
          </div>
          <div class="aa-ItemContentBody">
            <div class="aa-ItemContentTitle">
              <span class="title ${badges.query ? 'has-badge' : ''}" title=${item.name}>
                ${(item.name &&
                  components.Highlight({
                    hit: item,
                    attribute: 'name',
                  })) ||
                'Untitled'}
              </span>
              ${item.spaceType &&
              html`<span class="collection-icon title-icon">
                ${(item.collectionDefaultAccess || item.defaultAccess) === 'view' && html([viewIcon] as any)}
                ${(item.collectionDefaultAccess || item.defaultAccess) === 'edit' && html([queryIcon] as any)}
                ${(item.collectionDefaultAccess || item.defaultAccess) === 'restricted' && html([lockIcon] as any)}
              </span>`}
              ${badges.query && html(['<span class="badge blue">Query match</span>'] as any)}
              <div class="details">
                <span class="creator">${item.reportCreatorName || item.createdBy}</span>
                <span class="collection">
                  <div class="collection-wrapper">
                    ${!item.spaceType &&
                    html`<span class="collection-icon subtitle-icon">
                      ${(item.collectionDefaultAccess || item.defaultAccess) === 'view' && html([viewIcon] as any)}
                      ${(item.collectionDefaultAccess || item.defaultAccess) === 'edit' && html([queryIcon] as any)}
                      ${(item.collectionDefaultAccess || item.defaultAccess) === 'restricted' &&
                      html([lockIcon] as any)}
                    </span>`}
                    <span class="collection-name"> ${item.collectionName} </span>
                  </div>
                </span>
              </div>
            </div>
            <div class="aa-ItemContentDescription">
              ${item.description &&
              badges.description &&
              components.Highlight({
                hit: item,
                attribute: 'description',
              })}
            </div>
          </div>
          <div class="aa-ItemActions">
            <div class="new-tab-link">
              <span class="new-tab-link-text">Open</span>
              <span class="new-tab-link-icon">${html([newTabIcon] as any)}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  public transformResponse<TItem>(
    ...params: Parameters<TransformResponse<TItem>>
  ): TransformedRequesterResponse<TItem> {
    const [{ hits, results }] = params;
    const allItems = sortCollectionResults<TItem>(hits.flat(1));
    const query = (results[0] as SearchResponse<TItem>).query;

    if (query && allItems.length) {
      allItems.push({
        objectID: SHOW_MORE_RESULTS_ID,
        name: 'Show more results...',
        webPath: `/search?q=${encodeURIComponent(query).replace(/%20/g, '+')}`,
      } as unknown as Hit<TItem>);
    }

    return allItems;
  }

  public forceWidth(tmpl: string, color = 'inherit') {
    return tmpl.slice(0, 4) + ` style="width: 16px; color: ${color}" ` + tmpl.slice(4);
  }

  public parseBadges<TItem extends BaseItem>(item: TItem) {
    const hit = item as any;
    const badges = {
      description: false,
      query: false,
    };

    parseAlgoliaHitHighlight({ hit, attribute: 'description' }).map((x) => {
      if (x.isHighlighted) {
        badges.description = true;
      }
    });

    hit.queryKeywords?.map((value: string, i: number) => {
      parseAlgoliaHitHighlight({ hit, attribute: ['queryKeywords', i.toString()] }).map((x) => {
        if (x.isHighlighted) {
          badges.query = true;
        }
      });
    });

    return badges;
  }
}
